import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Package, Upload, Download, Trash2 } from 'lucide-react';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RawGoodsInventoryMain() {
  const navigate = useNavigate();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Delete authorization states
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  const modules = [
    { 
      title: 'Stock', 
      description: 'View current inventory levels', 
      icon: Package, 
      path: '/inventory/stock'
    },
    { 
      title: 'Receive', 
      description: 'Receive materials from purchase orders', 
      icon: Upload, 
      path: '/inventory/receive'
    },
    { 
      title: 'Issue', 
      description: 'Issue materials against work orders', 
      icon: Download, 
      path: '/inventory/issue'
    },
  ];

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch both receive and issue transactions
      const [receiveResponse, issueResponse] = await Promise.all([
        axios.get(`${API}/inventory/receive-transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/inventory/issue-transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      // Combine and mark transaction types
      const receiveTransactions = receiveResponse.data.map(txn => ({
        ...txn,
        transaction_type: 'RECEIVE',
        reference: txn.po_ref_number,
        quantity: txn.quantity_received
      }));
      
      const issueTransactions = issueResponse.data.map(txn => ({
        ...txn,
        transaction_type: 'ISSUE',
        reference: txn.wo_number,
        quantity: txn.quantity_issued
      }));
      
      // Combine all transactions
      const allTransactions = [...receiveTransactions, ...issueTransactions];
      
      // Sort by date (newest first)
      allTransactions.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });
      
      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to fetch inventory transactions');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 5 seconds, but pause when auth dialog is open
  useAutoRefresh(fetchTransactions, 5, authDialogOpen);

  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setAuthDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!transactionToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const endpoint = transactionToDelete.transaction_type === 'RECEIVE' 
        ? `${API}/inventory/receive-transactions/${transactionToDelete.id}`
        : `${API}/inventory/issue-transactions/${transactionToDelete.id}`;

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${transactionToDelete.transaction_type} transaction deleted successfully`);
      setAuthDialogOpen(false);
      setTransactionToDelete(null);
      
      // Refresh the list
      fetchTransactions();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('Failed to delete transaction');
    }
  };

  const getTransactionTypeColor = (type) => {
    return type === 'RECEIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-black">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Raw Goods Inventory
              </h1>
              <p className="text-sm text-gray-600 mt-1">Manage inventory stock, issuance, and receiving</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Module Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {modules.map((module) => {
            const Icon = module.icon;
            return (
              <div
                key={module.path}
                onClick={() => navigate(module.path)}
                className="bg-white border-2 border-black p-6 cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="w-16 h-16 bg-black flex items-center justify-center">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold tracking-tight uppercase mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {module.title}
                    </h3>
                    <p className="text-sm text-gray-600">{module.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Inventory Ledger Section */}
        <div className="bg-white border-2 border-black">
          <div className="bg-gray-50 border-b-2 border-black px-6 py-4">
            <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Inventory Activity Ledger
            </h2>
            <p className="text-sm text-gray-600 mt-1">Complete history of all receive and issue transactions</p>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 p-6 border-b border-gray-200">
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-gray-600 mb-1">Total Transactions</p>
              <p className="text-2xl font-bold">{transactions.length}</p>
            </div>
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-green-700 mb-1">Receive Transactions</p>
              <p className="text-2xl font-bold text-green-600">
                {transactions.filter(t => t.transaction_type === 'RECEIVE').length}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-red-700 mb-1">Issue Transactions</p>
              <p className="text-2xl font-bold text-red-600">
                {transactions.filter(t => t.transaction_type === 'ISSUE').length}
              </p>
            </div>
          </div>

          {/* Transactions Table */}
          {loading ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 text-lg">No transactions recorded yet</p>
              <p className="text-gray-400 text-sm mt-2">Start receiving or issuing materials to see transactions here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <thead className="bg-gray-50 border-b-2 border-black">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Reference</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Material Type</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Color</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Quantity</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Unit Price</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Total Value</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transactions.map(txn => {
                    const isFabric = txn.raw_good_type === 'FABRIC LN';
                    const unit = isFabric ? 'meters' : 'units';
                    const formattedDate = new Date(txn.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                    
                    return (
                      <tr key={`${txn.transaction_type}-${txn.id}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs">{formattedDate}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${getTransactionTypeColor(txn.transaction_type)}`}>
                            {txn.transaction_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium">{txn.reference}</td>
                        <td className="px-4 py-3 text-xs">{txn.raw_good_type}</td>
                        <td className="px-4 py-3 text-xs font-mono">{txn.code}</td>
                        <td className="px-4 py-3 text-xs">
                          <div className="font-mono text-xs">{txn.color_code}</div>
                          <div className="text-gray-600">{txn.color_name}</div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className={`font-semibold ${txn.transaction_type === 'RECEIVE' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.transaction_type === 'RECEIVE' ? '+' : '-'}{txn.quantity} {unit}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs">${txn.unit_price?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-xs font-semibold">${txn.total_value?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteClick(txn)}
                            className="text-red-600 border-2 border-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Authorization Dialog */}
        <AuthorizationDialog
          open={authDialogOpen}
          onOpenChange={setAuthDialogOpen}
          onAuthorized={handleDeleteConfirm}
          title="Delete Transaction"
          message={
            transactionToDelete 
              ? `Enter authorization code to delete ${transactionToDelete.transaction_type} transaction for ${transactionToDelete.code} - ${transactionToDelete.color_code} (${transactionToDelete.reference})`
              : 'Enter authorization code to proceed'
          }
        />
      </main>
    </div>
  );
}
